/**
 * The survey list — the first screen, and the one a surveyor lands on every
 * time they open the app from the home screen.
 *
 * Everything here is read from IndexedDB. There is no loading spinner tied to
 * the network and no empty list because a request failed: if the device has the
 * survey, the row is there.
 *
 * "+ New Survey" is spec §2.1's field-initiated path. It creates the survey
 * locally and kicks a drain; Chip and Tim are notified by the server the moment
 * that drain succeeds, which offline means "at the first bar of signal". The
 * row says which of those two states it is in rather than claiming a ✓ the
 * device cannot know about — see `summarize()` in src/ui/state/surveyList.ts.
 */

import { useState, type ReactNode } from 'react';

import { APP_BUILD } from '../../core/build';
import { Card, Eyebrow, SurveyChart, SurveyFilterBar, SwipeableRow } from '../components';
import { hrefFor } from '../routing';
import { navigate } from '../useRoute';
import { useServices } from '../state/services';
import { useCreateFieldSurvey, useSurveyList } from '../state/useSurveys';
import { EMPTY_FILTER, filterCount, filterSurveys, type SurveyFilter } from '../state/surveyFilter';
import { groupSurveys, type SurveySummary } from '../state/surveyList';
import type { StoredSurvey } from '../../db/store';

export function HomeScreen(): ReactNode {

  const { linked } = useServices();
  const { records, owed, loading, error } = useSurveyList();
  const createFieldSurvey = useCreateFieldSurvey();
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<SurveyFilter>(EMPTY_FILTER);

  const grouped = groupSurveys(records, owed);
  // Filtered per bucket, not over the flat list — that keeps each bucket's
  // own recency order (`groupSurveys` already sorted it) and lets the group
  // eyebrows below stay true: a group renders exactly when it still has rows
  // that match (Task 4).
  const shown = {
    assigned: filterSurveys(grouped.assigned, filter),
    field: filterSurveys(grouped.field, filter),
    submitted: filterSurveys(grouped.submitted, filter),
  };
  const totalShown = shown.assigned.length + shown.field.length + shown.submitted.length;
  const nothingYet = !loading && records.length === 0;
  // A filter that matches nothing is never an empty screen — one sentence and
  // the way out, beside it (Task 4).
  const noMatches = !nothingYet && filterCount(filter) > 0 && totalShown === 0;
  // `groupSurveys` reads nothing records.ts didn't already give it, so every
  // summary it hands back names a surveyId this map holds.
  const byId = new Map<string, StoredSurvey>(records.map((record) => [record.surveyId, record]));

  /**
   * One row (Task 3): the swipeable surface, and behind it the rail — Back up,
   * then Delete — that replaced the two buttons this task removed. Every row
   * opens the Building screen (round 7 task 10, ruling 13) — a SUBMITTED
   * survey read-only, an in-progress one editable, `useSurvey().readOnly`
   * being the only difference between them. From the design authority,
   * 2026-08-06: "Once 'Close and Submit' is pressed, the survey becomes
   * inaccessible, and there is no clear way to return to it" — it is
   * reachable, right here; and round 7: "When the user is on the home screen
   * and clicks on one of their submitted surveys, it should open the Building
   * Config screen in read-only mode."
   */
  const row = (summary: SurveySummary): ReactNode => {
    const record = byId.get(summary.surveyId);
    if (!record) return null;
    return <SwipeableRow key={summary.surveyId} record={record} summary={summary} />;
  };

  const startNew = (): void => {
    setCreating(true);
    void createFieldSurvey()
      .then((surveyId) => navigate({ name: 'building', surveyId }))
      // The survey is written to IndexedDB before this resolves, so a failure
      // here means the device could not store it at all — there is nothing to
      // navigate to and nothing queued.
      .catch(() => undefined)
      .finally(() => setCreating(false));
  };

  return (
    <>
      {/*
        * The unlinked card, rewritten by D-019.
        *
        * It used to say "open the link Chip or Tim texted you". Outbound SMS
        * has been parked indefinitely since the A2P denial (D-010), so that
        * sentence sent a surveyor away to wait for a text that will never
        * arrive — and it named a route that, on iOS, would have linked the
        * wrong storage container anyway. The way in is a setup code, taken
        * from inside this app, and the button goes to the screen that does it.
        */}
      {!linked ? (
        <Card>
          <p className="q" data-legibility="question">This device isn&rsquo;t linked to a surveyor yet.</p>
          {/* The headline and the way out — the how-to sentence is gone
            * (D-029 R1); the setup screen itself says scan-or-type. "Save a
            * backup instead" retired with Task 3: the swipeable row is that
            * function now, once there is a row to swipe. */}
          <a className="btn btn--primary btn--sync" href={hrefFor({ name: 'access' })}>
            Set up this device
          </a>
        </Card>
      ) : null}

      {error ? (
        <Card>
          <p className="q" data-legibility="question">Couldn&rsquo;t read this device&rsquo;s storage.</p>
          <p className="ruleline" data-legibility="helper">{error}</p>
        </Card>
      ) : null}

      {/*
        * The chart (D-035) — "above 'Your Surveys'", her words exactly.
        * `SurveyChart` reads its own 2+-survey threshold from `records.length`
        * (src/ui/components/SurveyChart.tsx) rather than this screen deciding
        * it a second time — same shape as the funnel below it.
        */}
      <SurveyChart records={records} />

      {/*
        * The job list at a glance (D-028): the day and title on the left, the
        * funnel and "+" on the right (Task 3). Her words: *"I don't like that
        * the filter button and the Add New Survey button sit below 'Your
        * Surveys.' They could be placed on the same line to the right, which
        * would shift the building row cards upward."* The funnel renders only
        * once the device holds 2+ surveys (Decision #10, `SurveyFilterBar`
        * enforces it from `eligible`); the "+" always renders — a phone with
        * 0 or 1 surveys still needs the one control that starts one.
        */}
      <div className="home-head">
        <div className="home-head__text">
          <p className="eyebrow" data-legibility="display">
            {today()}
          </p>
          <h1 className="sect" data-legibility="display">
            Your surveys
          </h1>
        </div>
        <div className="survey-toolbar">
          <SurveyFilterBar filter={filter} onChange={setFilter} eligible={records.length >= 2} />
          <button
            type="button"
            className="btn btn--plus"
            data-testid="new-survey"
            aria-label="New survey"
            aria-busy={creating}
            onClick={startNew}
            disabled={creating}
          >
            <PlusIcon />
          </button>
        </div>
      </div>

      {shown.assigned.length > 0 ? (
        <>
          <Eyebrow>Assigned to you</Eyebrow>
          {shown.assigned.map(row)}
        </>
      ) : null}

      {shown.field.length > 0 ? (
        <>
          {/* No eyebrow: work started here is the default state of the list —
           * the old group label over it was narration, not a state (D-029). */}
          {shown.field.map(row)}
        </>
      ) : null}

      {shown.submitted.length > 0 ? (
        <>
          <Eyebrow>Submitted</Eyebrow>
          {/* Said above the rows it is about: a submitted job has not vanished,
           * and tapping one is safe — it opens the record, not the form. */}
          {shown.submitted.map(row)}
        </>
      ) : null}

      {noMatches ? (
        <Card>
          <p className="q" data-legibility="question">No surveys match these filters.</p>
          <button
            type="button"
            className="btn btn--ghost"
            data-legibility="body"
            onClick={() => setFilter(EMPTY_FILTER)}
          >
            Clear filters
          </button>
        </Card>
      ) : null}

      {nothingYet ? (
        <>
          <p className="empty">
No surveys on this device yet.
          </p>
          {/* Decision #5. The device ROUTE stays — it is the only rescue path
            * for work sitting in the wrong iOS storage container (D-019) — but
            * it has no row of its own to swipe, so an empty list gets one
            * ghost link to it. Nothing here once the list holds a row: from
            * then on, backing up IS the swipe. */}
          <a className="btn btn--ghost" href={hrefFor({ name: 'device' })}>
            Open a backup file
          </a>
        </>
      ) : null}

      {/*
        * Round 8.2. The Device screen's own "Build {APP_BUILD}" (round 8.1)
        * only had a door onto it while the survey list was empty — the ghost
        * link above, gone the instant a row lands. Her words: *"I don't see
        * anything that says 'build.'"* This is the door that survives a full
        * list: one quiet line, always here, always a link to the screen that
        * says the rest (D-029 — a build stamp is diagnostic, not content).
        */}
      <a className="home__build" href={hrefFor({ name: 'device' })}>
        <p className="ruleline home__build" data-legibility="helper">Build {APP_BUILD}</p>
      </a>
    </>
  );
}

/** "Sunday, Aug 30" — the surveyor's own day, for the head of the list. */
function today(): string {
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric' }).format(new Date());
}

/** Hand-drawn — Constraint 1, no icon library. A plus: two strokes, square-capped. */
function PlusIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path
        d="M12 4.5v15M4.5 12h15"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}
